import {
  DataGridPremium,
  DataGridPremiumProps,
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridFilterModel,
  GridInitialState,
  GridRow,
  GridRowGroupingModel,
  GridRowId,
  GridRowParams,
  MuiEvent,
  useGridApiRef,
} from '@mui/x-data-grid-premium'
import isArray from 'lodash/isArray'
import isNil from 'lodash/isNil'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ReactNode, useCallback, useMemo, useState } from 'react'
import { trySaveToLocalStorage } from '@omnea-app/fe-browser-utils/trySaveToLocalStorage'
import { isDefined } from '@omnea-app/legacy/utils/type-guard/isDefined'
import { CustomPagination } from './CustomPagination'
import { DataGridContainer } from './DataGridContainer'
import { DataGridLoading } from './DataGridLoading'
import { DefaultNoRowsState } from './DefaultNoRowsState'
import { useDataGridAnalytics } from './hooks/useDataGridAnalytics'
import { useHasScrolledHorizontally } from './hooks/useHasScrolledHorizontally'
import { useMuiDataGridLocaleText } from './hooks/useMuiDataGridLocaleText'
import { usePreserveScrollPosition } from './hooks/usePreserveScrollPosition'
import { useEditingRowsContext } from './RowEditingContext'
import { mergeColumnOrder } from './utils/mergeColumnOrder'

export type TableEditMode = 'row' | 'cell'

export interface DataGridBaseProps extends DataGridPremiumProps {
  stateKey?: string | null
  defaultSortModel?: {
    field: string
    sort: 'asc' | 'desc'
  }
  initialColumnVisibility?: GridColumnVisibilityModel
  getDefaultRowLink?: (id: GridRowId) => string | null
  hasUncontainedTableStyling?: boolean
  allowPageScroll?: boolean
  pinnedColumns?: {
    left?: string[]
    right?: string[]
  }
  isDefaultToolbarHidden?: boolean
  editMode?: TableEditMode
  TableEmptyState?: () => ReactNode
  onRowClick?: (params: GridRowParams, event: MuiEvent<React.MouseEvent<HTMLElement>>, details: GridCallbackDetails) => void
  pageSizeOptions?: number[]
  hasExternalHeader?: boolean
  totalCountLoading?: boolean
}

export const DataGridBase = ({
  sx,
  stateKey,
  loading,
  defaultSortModel,
  initialColumnVisibility,
  slots,
  getDefaultRowLink,
  hasUncontainedTableStyling,
  allowPageScroll,
  pinnedColumns,
  isDefaultToolbarHidden,
  hideFooter,
  apiRef: apiRefProp,
  editMode = 'row',
  TableEmptyState = DefaultNoRowsState,
  onRowClick,
  onFilterModelChange,
  filterModel,
  pageSizeOptions = [10, 25, 50, 100],
  hasExternalHeader = false,
  totalCountLoading = false,
  rowCount,
  ...props
}: DataGridBaseProps) => {
  const localeText = useMuiDataGridLocaleText()
  const defaultApiRef = useGridApiRef()
  const apiRef = useState(apiRefProp ?? defaultApiRef)[0]
  const { currentlyEditingRow } = useEditingRowsContext()
  const pathname = usePathname()

  const localStorageKey = isDefined(stateKey) ? getTableStateLocalStorageKey(stateKey) : null
  const initialState = useMemo(() => getLocalStorageState(localStorageKey), [localStorageKey])

  // IF we have unstable datasource don't set the grouping model
  const [rowGroupingModel, setRowGroupingModel] = useState<GridRowGroupingModel>(
    props.unstable_dataSource ? [] : (initialState.rowGrouping?.model ?? [])
  )
  const sortModel = useMemo(() => (defaultSortModel ? [defaultSortModel] : undefined), [defaultSortModel])

  // Order columns based on stored table state
  const orderedColumns = useMemo(() => {
    const tableState = getLocalStorageState(localStorageKey)
    const orderedFields = tableState.columns?.orderedFields
    if (!isArray(orderedFields) || props.disableColumnReorder) {
      return props.columns
    }

    // NOTE (Dalyan): We need to ensure that the persisted column widths from localStorage
    // are used for the returned `orderingColumns` value - otherwise, it will set the column width
    // to the default value defined by us in the column config, or the MUI default of `100`
    const columnsWithWidth = props.columns.map((column) => {
      const width = (tableState.columns?.dimensions ?? {})[column.field]?.width
      return { ...column, ...(isDefined(width) && { width }) }
    })

    return mergeColumnOrder(columnsWithWidth, orderedFields)
  }, [props.columns, localStorageKey, props.disableColumnReorder])

  const handleStateChange = useCallback(() => {
    if (isDefined(localStorageKey)) {
      const exportedState = apiRef?.current?.exportState()
      if (isDefined(exportedState)) {
        trySaveToLocalStorage({ key: localStorageKey, value: JSON.stringify(exportedState) })
      }
    }
  }, [apiRef, localStorageKey])

  const hasScrolledHorizontally = useHasScrolledHorizontally(apiRef)
  const hasFilterPanel = isDefined(slots?.filterPanel)

  const { trackRowClick, trackFilterChange } = useDataGridAnalytics({
    loading: !!loading,
    rows: Array.from(props.rows ?? []),
    tableName: isDefined(stateKey) ? stateKey : (pathname ?? 'unknown_table'),
    filterModel,
    sortModel,
    initialState,
    rowGroupingModel,
  })

  // Preserve scroll position across re-renders
  usePreserveScrollPosition({
    apiRef,
    rows: props.rows ?? [],
    stateKey,
  })

  const handleRowClick = (
    params: GridRowParams,
    event: MuiEvent<React.MouseEvent<HTMLElement>>,
    details: GridCallbackDetails
  ) => {
    trackRowClick()
    if (onRowClick) {
      onRowClick(params, event, details)
    }
  }

  const handleFilterModelChange = (updatedFilterModel: GridFilterModel, details: GridCallbackDetails) => {
    trackFilterChange(updatedFilterModel, details)
    if (onFilterModelChange) {
      onFilterModelChange(updatedFilterModel, details)
    }
  }

  // Check if we're using V2 data source pattern
  const isUsingDataSource = !!props.unstable_dataSource

  const roundTopCorners = !(hasFilterPanel || hasExternalHeader)

  return (
    <DataGridContainer
      isDefaultToolbarHidden={isDefaultToolbarHidden}
      hideFooter={hideFooter}
      allowPageScroll={allowPageScroll}
      hasExternalHeader={hasExternalHeader}
    >
      {loading && props.rows?.length === 0 ? (
        <DataGridLoading hasUncontainedTableStyling={hasUncontainedTableStyling} />
      ) : (
        <DataGridPremium
          editMode={editMode}
          getRowClassName={(params) => (String(params.id).includes('auto-generated-row') ? 'grouping-row' : '')}
          onProcessRowUpdateError={(error) => console.error('Failed to process row update', { error })}
          pagination
          disableRowSelectionOnClick
          onRowClick={handleRowClick}
          {...(!isUsingDataSource && { onFilterModelChange: handleFilterModelChange })}
          loading={loading}
          columnHeaderHeight={40}
          pageSizeOptions={pageSizeOptions}
          filterDebounceMs={500}
          sx={{
            border: 'none',
            '--DataGrid-overlayHeight': '300px',

            '.MuiDataGrid-toolbarContainer': {
              border: 'none !important',
            },
            '.MuiDataGrid-columnHeaders': {
              // Don't round the top corners if there is a filter panel or external header above it
              borderTopLeftRadius: roundTopCorners ? '8px' : '0px',
              borderTopRightRadius: roundTopCorners ? '8px' : '0px',
              borderBottom: 'none',

              '& .MuiDataGrid-row--borderBottom': {
                borderTopLeftRadius: hasFilterPanel ? '0px' : '8px',
                borderTopRightRadius: hasFilterPanel ? '0px' : '8px',
              },
            },
            '.MuiDataGrid-columnHeadersInner': {
              overflow: 'visible',
            },
            '.MuiDataGrid-columnHeader': {
              overflow: 'visible',
            },
            '.MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainerContent': {
              justifyContent: 'center',
            },
            '.MuiDataGrid-columnHeader--pinned': {
              zIndex: 2,
              backgroundColor: 'background.paper',
            },
            '& .MuiDataGrid-footerContainer': {
              borderTop: 'none !important',
            },
            '& .MuiDataGrid-row': {
              border: '1px solid #E0E0E0 !important',
              borderTop: 'none !important',
            },
            // Last row (may or may not be wrapped in a link)
            '& :not(a) > .MuiDataGrid-row:last-child, & a:last-child > .MuiDataGrid-row': {
              borderBottomLeftRadius: hideFooter ? '8px' : '0px',
              borderBottomRightRadius: hideFooter ? '8px' : '0px',
              '& .MuiDataGrid-cell:first-of-type': {
                borderBottomLeftRadius: hideFooter ? '8px' : 0,
              },
              '& .MuiDataGrid-cell:last-of-type': {
                borderBottomRightRadius: hideFooter ? '8px' : 0,
              },
            },
            '& .MuiDataGrid-filler': {
              border: 'none',
              '&>div': {
                border: 'none',
              },
            },
            '& .MuiDataGrid-columnSeparator': {
              opacity: 0,
            },
            '& .MuiDataGrid-cell': {
              border: 'none', // Ensures cells don't add extra borders
              alignItems: 'center',
              display: 'flex',
            },
            '& .MuiDataGrid-headerCell': {
              border: 'none', // Ensures header cells don't add extra borders
            },
            '& .MuiDataGrid-virtualScroller': {
              border: 'none', // Ensures the virtual scroller doesn't add extra borders
              overscrollBehavior: allowPageScroll ? 'auto' : 'none', // Prevents scroll overshoot/scroll chaining when allowPageScroll is false; uses default scrolling when true
              // Disable vertical scrolling when table is empty to allow page scroll (preserve horizontal for column headers)
              ...(props.rows && props.rows.length === 0 && { overflowY: 'hidden !important' }),
              // Don't round the top corners if there is a filter panel or external header above it
              borderTopLeftRadius: roundTopCorners ? '8px' : '0px',
              borderTopRightRadius: roundTopCorners ? '8px' : '0px',
            },
            '& .MuiDataGrid-overlayWrapperInner': {
              border: '1px solid #E7E7ED',
              borderBottomLeftRadius: hideFooter ? '8px' : '0px',
              borderBottomRightRadius: hideFooter ? '8px' : '0px',
              borderTop: 'none',
            },
            // If the Grid contains a loading spinner, make the contant semi-transparent for contrast
            '&:has(.MuiCircularProgress-root) .MuiDataGrid-virtualScroller': {
              overflow: 'hidden',
            },
            ...sx,
            '& .MuiDataGrid-toolbarContainer': { p: 0, m: 0 },
            '& .grouping-row': {
              backgroundColor: '#F6F7FA',
              '&.Mui-selected': {
                backgroundColor: '#F6F7FA',
              },
            },
            '& .archived-row': {
              backgroundColor: 'neutralLight.100',
              '& .MuiDataGrid-cell': {
                color: 'neutralLight.900',
              },
            },
            '& .MuiDataGrid-columnHeader--pinnedLeft': {
              borderTopLeftRadius: roundTopCorners ? '0px' : '8px',
            },
            '& .MuiDataGrid-cell--pinnedLeft': {
              boxShadow: hasScrolledHorizontally ? '8px 0px 15px -8px #D1D1D1' : 'none',
              borderBottom: '1px solid #E0E0E0',
              backgroundColor: 'white',
            },
            '& .MuiDataGrid-cell--pinnedRight': {
              borderBottom: '1px solid #E0E0E0',
              backgroundColor: 'white',
            },
          }}
          onStateChange={handleStateChange}
          showColumnVerticalBorder
          localeText={localeText}
          slots={{
            noRowsOverlay: isNil(currentlyEditingRow) ? TableEmptyState : () => null,
            noResultsOverlay: isNil(currentlyEditingRow) ? TableEmptyState : () => null,
            row: (rowProps) => {
              const isGroupedRowDropdownMenu = String(rowProps.rowId).match(/^auto-generated-row/)
              if (isGroupedRowDropdownMenu) return <GridRow {...rowProps} />

              const rowLink = getDefaultRowLink?.(rowProps.rowId)

              if (typeof rowLink === 'string' && currentlyEditingRow !== rowProps.rowId.toString()) {
                return (
                  <Link href={rowLink}>
                    <GridRow {...rowProps} />
                  </Link>
                )
              }

              if (typeof rowLink !== 'string') return <GridRow {...rowProps} />
              return (
                <Link href={rowLink}>
                  <GridRow {...rowProps} />
                </Link>
              )
            },
            ...(totalCountLoading && {
              pagination: () => <CustomPagination totalCountLoading />,
            }),
            ...slots,
          }}
          initialState={{
            sorting: {
              sortModel,
            },
            ...initialState,
            ...(pinnedColumns && { pinnedColumns }),
          }}
          columnVisibilityModel={initialColumnVisibility}
          pinnedColumns={rowGroupingModel.length === 0 ? pinnedColumns : undefined}
          groupingColDef={{ leafField: pinnedColumns?.left?.[0] }}
          hideFooter={hideFooter}
          // Conditional props based on data source usage
          {...(isUsingDataSource
            ? {
                // V2 Pattern: Use data source with explicit props
                unstable_dataSource: props.unstable_dataSource,
                slotProps: props.slotProps,
                ...(rowCount != null && { rowCount }),
                disableAggregation: props.disableAggregation,
                filterModel,
                onFilterModelChange: handleFilterModelChange,
                processRowUpdate: props.processRowUpdate,
                paginationModel: props.paginationModel,
                onPaginationModelChange: props.onPaginationModelChange,
                columnVisibilityModel: props.columnVisibilityModel,
                onColumnVisibilityModelChange: props.onColumnVisibilityModelChange,
                unstable_dataSourceCache: null,
              }
            : {
                // V1 Pattern: Use traditional row-based props with filter handling
                ...props,
                filterModel,
                ...(rowCount != null && { rowCount }),
              })}
          apiRef={apiRef}
          columns={orderedColumns}
          rowGroupingModel={rowGroupingModel}
          onRowGroupingModelChange={(model) => setRowGroupingModel(model)}
        />
      )}
    </DataGridContainer>
  )
}

function getLocalStorageState(key: string | null): GridInitialState {
  try {
    const stateFromLocalStorage = isDefined(key) ? localStorage.getItem(key) : null
    return isDefined(stateFromLocalStorage) ? JSON.parse(stateFromLocalStorage) : {}
  } catch (error) {
    console.error('Error getting table state from local storage', error)
    return {}
  }
}

export function getTableStateLocalStorageKey(name: string): string {
  return `${name}_TABLE_STATE`
}
